/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, April 28th 2023, 7:30:15 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useGlobalScope } from "core/providers";
import { useAPIFetchItem, useAPIRequest, APISERVICEPATHS, useAPIForm } from "lib/api";
import {
  IntegrationConnection,
  IntegrationConnectionValidationSchema,
  IntegrationProvider,
  IntegrationCredentialStore,
  CredentialStoreValidationSchema,
  TaskTemplate,
} from "lib/schemas";
import { useIntegrationStore } from "integrations/stores";
import { useDeleteOSItem } from "version-control/services";

const integrationProvidersAPIServicePath = APISERVICEPATHS.integrationProviders;

const useAPIFetchIntegrationProvider = () =>
  useAPIFetchItem<IntegrationProvider>({
    apiServicePath: integrationProvidersAPIServicePath,
    makeRequestOnInitialMount: false,
  });

const useAPIListIntegrationProviders = () => {
  const { makeRequest, ...rest } = useAPIRequest<IntegrationProvider[]>({
    apiServicePath: integrationProvidersAPIServicePath,
    makeRequestOnInitialMount: false,
  });

  const { integrationProviders, populateIntegrationProviders } = useIntegrationStore();

  const fetchProviders = React.useCallback(() => {
    if (integrationProviders?.length) return;
    makeRequest({ setValidAPIResponse: populateIntegrationProviders });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(integrationProviders)]);

  return { integrationProviders, fetchProviders, ...rest };
};

const integrationConnectionsAPIPath = APISERVICEPATHS.integrationConnections;

const useAPIListIntegrationConnections = () => {
  const { makeRequest, ...rest } = useAPIRequest<IntegrationConnection[]>({
    apiServicePath: integrationConnectionsAPIPath,
    makeRequestOnInitialMount: false,
  });

  const { integrationConnections, addIntegrationConnection } = useIntegrationStore();

  const fetchConnections = React.useCallback(
    (args: { loadAll?: boolean; taskTemplateConnectionIds?: TaskTemplate["integration_connections"] }) => {
      const { taskTemplateConnectionIds = [], loadAll = false } = args;

      if (!Boolean(taskTemplateConnectionIds?.length) && !loadAll) return;

      if (
        integrationConnections?.length &&
        integrationConnections.every((connection) => taskTemplateConnectionIds.includes(connection.id))
      )
        return;

      makeRequest({
        apiQueryParams: { id: taskTemplateConnectionIds },
        setValidAPIResponse: (connections) => {
          connections.forEach((connection) => addIntegrationConnection(connection));
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addIntegrationConnection, makeRequest, JSON.stringify(integrationConnections)]
  );

  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  return {
    integrationConnections: integrationConnections.filter((c) => c.organization === selectedOrganization?.id),
    fetchConnections,
    ...rest,
  };
};

interface UseIntegrationConnectionAPIFormSpec {
  integrationConnectionFetchId: IntegrationConnection["id"];
  integrationConnectionDefaultValues: IntegrationConnection;
  updateIntegrationConnectionFromAPICallback: (connection: IntegrationConnection) => void;
}

const useIntegrationConnectionAPIForm = ({
  integrationConnectionFetchId,
  integrationConnectionDefaultValues,
  updateIntegrationConnectionFromAPICallback,
}: UseIntegrationConnectionAPIFormSpec) => {
  const useAPIFormReturn = useAPIForm<IntegrationConnection>({
    apiServicePath: integrationConnectionsAPIPath,
    apiFormDataValidationSchema: IntegrationConnectionValidationSchema,
    apiFormDefaultValues: integrationConnectionDefaultValues,
    apiInitialFetchId: integrationConnectionFetchId,
    primaryKey: "id",
    setAPIFormResponse: updateIntegrationConnectionFromAPICallback,
    formResetDependencyArray: [integrationConnectionDefaultValues?.id],
  });

  return useAPIFormReturn;
};

const useDeleteIntegrationConnection = () => useDeleteOSItem<IntegrationConnection>(integrationConnectionsAPIPath);

interface UseCredentialStoreAPIFormAPIFormSpec {
  credentialStoreFetchId: IntegrationCredentialStore["id"];
  credentialStoreDefaultValues: IntegrationCredentialStore;
  updateCredentialStoreFromAPICallback: (credentialStore: IntegrationCredentialStore) => void;
}

const credentialStoresAPIPath = APISERVICEPATHS.integrationCredentialStores;

const useCredentialStoreAPIForm = ({
  credentialStoreFetchId,
  credentialStoreDefaultValues,
  updateCredentialStoreFromAPICallback,
}: UseCredentialStoreAPIFormAPIFormSpec) => {
  const useAPIFormReturn = useAPIForm<IntegrationCredentialStore>({
    apiServicePath: credentialStoresAPIPath,
    apiFormDataValidationSchema: CredentialStoreValidationSchema,
    apiFormDefaultValues: credentialStoreDefaultValues,
    apiInitialFetchId: credentialStoreFetchId,
    primaryKey: "id",
    setAPIFormResponse: updateCredentialStoreFromAPICallback,
  });

  return useAPIFormReturn;
};

const useAPIListCredentialStores = () => {
  const { makeRequest, ...rest } = useAPIRequest<IntegrationCredentialStore[]>({
    apiServicePath: credentialStoresAPIPath,
    makeRequestOnInitialMount: false,
  });

  const fetchCredentialStores = React.useCallback(
    (onFetchList: (credentialStores: IntegrationCredentialStore[]) => void) => {
      makeRequest({ setValidAPIResponse: onFetchList });
    },
    [makeRequest]
  );

  return { fetchCredentialStores, ...rest };
};

const decryptCredentialsAPIPath = APISERVICEPATHS.decryptCredentials;

const useDecryptCredentials = () => {
  const { makeRequest, ...rest } = useAPIRequest<any>({
    apiServicePath: decryptCredentialsAPIPath,
    makeRequestOnInitialMount: false,
  });

  const decryptCredentials = (
    credentialStoreId: IntegrationCredentialStore["id"],
    password: string,
    onDecrypt: (credentials: any) => void
  ) => {
    makeRequest({
      method: "POST",
      apiPathParams: [credentialStoreId],
      body: JSON.stringify({ password }),
      setValidAPIResponse: onDecrypt,
    });
  };

  return { decryptCredentials, ...rest };
};

export const useIntegrationsAPI = () => ({
  useAPIFetchIntegrationProvider,
  useAPIListIntegrationProviders,
  useIntegrationConnectionAPIForm,
  useAPIListCredentialStores,
  useCredentialStoreAPIForm,
  useAPIListIntegrationConnections,
  useDeleteIntegrationConnection,
  useDecryptCredentials,
});
